import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useDispatchToStore } from '@hooks/reduxHooks';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import getPlusSubscriptionsUids from '@utils/subscriptionHelpers/getPlusSubscriptionUIDs';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import {
  fetchTopologyEnrolledCourses,
  fetchRecommendedCourses,
  fetchCompletedCourses
} from '@actions/courses';
// import { fetchTopologyNotes } from '@actions/topologies';
import breadcrumbs, { goBack } from '@utils/breadcrumbs';
import { getImage } from '@utils/imageHelper';
import Paper from '@base/Paper/Paper';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import H1 from '@base/H1/H1';
import {
  getGoalInfoForCoursesPage,
  getTopicInfoForCoursesPage
} from '@selectors/syllabus';
import { getMeUid } from '@selectors/auth';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import {
  getIsSubscribedToPlus,
  getIsSubscribedToLite,
  getIsSubscribedToOffline
} from '@selectors/subscription';
import { useIsMobile } from '@stores/AppCommonData';
import EnrolledCourses from './EnrolledCourses';
import CompletedCourses from './CompletedCourses';
import MoreCourses from './MoreCourses';
import RecommendedCourses from './RecommendedCourses';
import PreSubscriptionBanner from '../PlusTopics/PreSubscriptionBanner';
import BlockWrapper from './BlockWrapper';
// import Notes from './Notes';

const HeaderNavBar = styled(Paper)`
  margin-bottom: ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_12B} ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    background: var(--color-base-0);
    box-shadow: none;
    padding: ${Spacings.SPACING_6B} 0 0 0;
    margin-bottom: ${Spacings.SPACING_8B};
  }
`;

const Title = styled(H1)`
  margin-top: ${Spacings.SPACING_6B};
  color: var(--color-text-primary);
  font-size: ${Fonts.H2};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
`;

const MoreCoursesWrapper = styled(BlockWrapper)`
  ${mediaQuery.uptoMobile} {
    grid-template-columns: repeat(10, 1fr);
    grid-column-gap: ${Spacings.SPACING_2B};
    grid-row-gap: 0;
    grid-column: span 10;
    position: relative;
    & > div[orientation='vertical'] {
      margin-bottom: ${Spacings.SPACING_6B};
    }
  }
`;

const bannerImageProps = {
  src: getImage('pre_subscription_courses.svg'),
  size: '48px'
};

const getHeaderTags = (goalTopology, topicTopology, isOfflineSubscription) => {
  const tags = [];
  if (goalTopology) {
    tags.push(
      ...breadcrumbs.getPlusTopicsTags(goalTopology, isOfflineSubscription)
    );
    tags.push(...breadcrumbs.getTopics(goalTopology));
  }
  if (topicTopology && topicTopology.parent) {
    tags.push(
      ...breadcrumbs.getTopologyTags(topicTopology.parent, goalTopology)
    );
  }
  return tags;
};

const PlusCourses = ({
  goalUID,
  topicUid,
  showRecommended,
  isContentAvailable,
  topicTopology,
  isSubscribed
}) => {
  const meUid = useSelector(getMeUid);
  const dispatch = useDispatchToStore();

  // refresh data after login
  useFirstRenderSkip(() => {
    if (isSubscribed) {
      const timestampParam = `&timestamp=${Date.now()}`;
      dispatch(
        fetchTopologyEnrolledCourses(topicUid, true, true, timestampParam)
      );
      dispatch(fetchRecommendedCourses(topicUid, true, true, timestampParam));
      dispatch(fetchCompletedCourses(topicUid, true, true, timestampParam));
      // dispatch(fetchTopologyNotes(topicUid, timestampParam));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meUid]);

  return (
    <>
      {/* <Notes topic={topicTopology} goalUID={goalUID} key="topology-notes" /> */}
      <EnrolledCourses key="enrolled-courses" topic={topicTopology} />
      {showRecommended && (
        <RecommendedCourses key="recommended-courses" topic={topicTopology} />
      )}
      <CompletedCourses key="completed-courses" topic={topicTopology} />
      <MoreCoursesWrapper>
        <MoreCourses
          key="more-courses"
          topic={topicTopology}
          isContentAvailable={isContentAvailable}
          goalUID={goalUID}
        />
      </MoreCoursesWrapper>
    </>
  );
};

PlusCourses.propTypes = {
  goalUID: PropTypes.string.isRequired,
  topicUid: PropTypes.string.isRequired,
  showRecommended: PropTypes.bool.isRequired,
  isContentAvailable: PropTypes.bool.isRequired,
  topicTopology: PropTypes.objectOf(PropTypes.any).isRequired,
  isSubscribed: PropTypes.bool.isRequired
};

const PlusCoursesWrapper = ({
  goalUID,
  topicUid,
  showRecommended,
  isContentAvailable
}) => {
  const isMobile = useIsMobile();
  const isLiteUser = useSelector(getIsSubscribedToLite(goalUID));
  const goalTopology = useSelector((state) =>
    getGoalInfoForCoursesPage(state, goalUID)
  );
  const topicTopology = useSelector((state) =>
    getTopicInfoForCoursesPage(state, topicUid)
  );
  const meData = useMyInfo(getMe);
  const isSubscribed =
    useSelector(getIsSubscribedToPlus(goalUID)) ||
    getPlusSubscriptionsUids(meData)?.includes(goalUID.toUpperCase());
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUID));

  const title = `${goalTopology.name} ${topicTopology.name || ''}`;

  const renderPreSubscriptionContent = () => {
    if (isLiteUser) return null;

    return (
      <PreSubscriptionBanner
        imageProps={bannerImageProps}
        title="Subscribe and access"
        content="Complete syllabus with Structured Courses"
        goalUID={goalUID}
      />
    );
  };
  return (
    <>
      <HeaderNavBar gridColumn={isMobile ? 'span 10' : 'span 18'}>
        <BreadCrumbs
          tags={getHeaderTags(
            goalTopology,
            topicTopology,
            isOfflineSubscription
          )}
          onBackClick={goBack}
          useBackClick
        />
        <Title lineClamp={1}>
          <span title={title}>{title}</span>
        </Title>
      </HeaderNavBar>
      {!(isSubscribed || isOfflineSubscription) &&
        renderPreSubscriptionContent()}
      <PlusCourses
        goalUID={goalUID}
        topicUid={topicUid}
        showRecommended={showRecommended}
        isContentAvailable={isContentAvailable}
        topicTopology={topicTopology}
        isSubscribed={isSubscribed || isOfflineSubscription}
      />
    </>
  );
};

PlusCoursesWrapper.propTypes = {
  goalUID: PropTypes.string.isRequired,
  topicUid: PropTypes.string.isRequired,
  showRecommended: PropTypes.bool.isRequired,
  isContentAvailable: PropTypes.bool.isRequired
};

export default PlusCoursesWrapper;
